/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Pipeline Orchestrator
 * ============================================================================
 * 
 * Orquestrador principal que integra:
 * - ConversationStateMachine (controle de fluxo)
 * - TurnDecisionEngine (decisão de fim de turno)
 * - AudioCapture (captura de microfone)
 * - STT (Speech-to-Text via Web Speech API)
 * - Fallback System (garantia contra travamentos)
 * 
 * SPRINT 2: Mobile-First Voice Engine
 * - PlatformConfigurationManager (configuração por plataforma)
 * - MobileAudioEngine (AudioContext unlock, recovery)
 * - MobileDiagnostics (logs detalhados)
 * 
 * SPRINT 3A: Event Sequence Investigation
 * - MobileEventSequenceLogger (instrumentação completa de eventos)
 * - Single Source of Truth para idioma
 * 
 * Este módulo substitui a lógica distribuída do voiceProcessor.js antigo,
 * centralizando a orquestração e eliminando pontos únicos de falha.
 * 
 * @author Vocalira Team
 * @version 2.2.0 - SPRINT 3A Event Investigation
 */

import TurnDecisionEngine from './TurnDecisionEngine.js';
import ConversationStateMachine, { ConversationState } from './ConversationStateMachine.js';
import { getPlatformConfig } from './PlatformConfigurationManager.js';
import { 
  AudioContextManager, 
  MediaStreamManager, 
  STTRecoveryManager,
  MobileRecoveryEngine 
} from './MobileAudioEngine.js';
import { getDiagnostics, DiagnosticLevel } from './MobileDiagnostics.js';
import mobileEventLogger from './MobileEventSequenceLogger.js';

// ============================================================================
// CONFIGURAÇÕES (agora mescladas com PlatformConfig)
// ============================================================================

const DEFAULT_CONFIG = {
  // Audio capture
  sampleRate: 16000,
  fftSize: 2048,
  
  // VAD thresholds (mais permissivos que antes)
  vadSpeechThreshold: 0.015,      // Era 0.018
  vadSilenceThreshold: 0.012,     // Era 0.008 (muito baixo!)
  vadSpeechStartDelay: 80,        // ms de debounce para início
  
  // Noise floor
  noiseFloorEstimationTime: 500,  // ms para estimar ruído
  noiseFloorMultiplier: 1.5,      // threshold = noiseFloor * multiplier
  
  // Voice frequency analysis
  voiceFreqMin: 85,               // Hz
  voiceFreqMax: 3000,             // Hz
  voiceFreqRatio: 0.45,           // % de energia em frequências de voz
  
  // Continuous mode
  autoListenDelay: 800,           // ms antes de auto-listen
  autoListenEnabled: true,
  
  // Safety
  maxRecordingTime: 60000,        // 60 segundos máximo
  
  // Debug
  debugEnabled: false,
};

// ============================================================================
// CLASSE PRINCIPAL: VCCEPipeline
// ============================================================================

/**
 * Pipeline orquestrador do VCCE.
 * 
 * SPRINT 2: Agora integrado com Mobile-First Architecture:
 * - PlatformConfigurationManager para configurações por plataforma
 * - AudioContextManager para unlock e gerenciamento mobile
 * - STTRecoveryManager para restart inteligente
 * - MobileDiagnostics para logging detalhado
 * 
 * Responsabilidades:
 * - Coordenar todos os componentes do pipeline
 * - Gerenciar ciclo de vida do áudio
 * - Integrar STT com Decision Engine
 * - Garantir que o pipeline nunca trave
 */
class VCCEPipeline {
  constructor(options = {}) {
    // SPRINT 2: Obter configuração da plataforma
    this.platformConfig = getPlatformConfig();
    this.platformSettings = this.platformConfig.getConfig();
    this.platform = this.platformConfig.getPlatform();
    
    // SPRINT 2: Inicializar diagnósticos
    this.diagnostics = getDiagnostics();
    
    // Mesclar configurações: DEFAULT < PLATFORM < OPTIONS
    this.config = { 
      ...DEFAULT_CONFIG, 
      ...this.platformSettings,
      ...options 
    };
    
    // Componentes principais
    this.stateMachine = new ConversationStateMachine({
      onStateChange: this._onStateChange.bind(this),
      onLog: this._log.bind(this),
    });
    
    this.decisionEngine = new TurnDecisionEngine({
      onTurnComplete: this._onTurnComplete.bind(this),
      onDecisionUpdate: this._onDecisionUpdate.bind(this),
      onLog: this._log.bind(this),
    });
    
    // SPRINT 2: Mobile Audio Engine components
    this.audioContextManager = new AudioContextManager({
      onStateChange: this._onAudioContextStateChange.bind(this),
      onError: this._onAudioError.bind(this),
      onLog: this._log.bind(this),
    });
    
    this.mediaStreamManager = new MediaStreamManager({
      onLog: this._log.bind(this),
      onError: this._onAudioError.bind(this),
    });
    
    this.sttRecoveryManager = new STTRecoveryManager({
      onLog: this._log.bind(this),
      onRecoveryExhausted: this._onSTTRecoveryExhausted.bind(this),
    });
    
    this.recoveryEngine = new MobileRecoveryEngine({
      audioContextManager: this.audioContextManager,
      mediaStreamManager: this.mediaStreamManager,
      sttRecoveryManager: this.sttRecoveryManager,
      onLog: this._log.bind(this),
      onRecoveryStart: () => this.callbacks.onError('Recovering voice capture...'),
      onRecoveryComplete: () => this._log('RECOVERY_COMPLETE'),
      onRecoveryFailed: (msg) => this.callbacks.onError(msg),
    });
    
    // Audio components (agora gerenciados pelos managers)
    this.audioContext = null;
    this.analyser = null;
    this.mediaStream = null;
    this.mediaSource = null;
    
    // Speech Recognition
    this.recognition = null;
    this.recognitionActive = false;
    
    // Estado interno
    this.noiseFloor = 0.01;
    this.adaptiveNoiseFloor = 0.01;
    this.smoothedVolume = 0;
    this.volumeHistory = [];
    
    // Transcrição
    this.transcript = '';
    this.interimTranscript = '';
    
    // Callbacks externos
    this.callbacks = {
      onStateChange: options.onStateChange || (() => {}),
      onTranscript: options.onTranscript || (() => {}),
      onInterimTranscript: options.onInterimTranscript || (() => {}),
      onTurnComplete: options.onTurnComplete || (() => {}),
      onVolumeChange: options.onVolumeChange || (() => {}),
      onError: options.onError || (() => {}),
      onDecisionUpdate: options.onDecisionUpdate || (() => {}),
    };
    
    // Animation frame
    this.animationFrameId = null;
    
    // Métricas
    this.metrics = {
      sessionsStarted: 0,
      turnsCompleted: 0,
      errors: 0,
      avgTurnDuration: 0,
    };
    
    // SPRINT 2: Log de inicialização
    this.diagnostics.log(DiagnosticLevel.INFO, 'VCCE_PIPELINE_INITIALIZED', {
      platform: this.platform.platformId,
      config: {
        vadSpeechThreshold: this.config.vadSpeechThreshold,
        vadSilenceThreshold: this.config.vadSilenceThreshold,
        sttRestartDelay: this.config.sttRestartDelay,
      },
    });
    
    this._log('VCCE_PIPELINE_INITIALIZED', { config: this.config });
  }
  
  // ==========================================================================
  // SPRINT 2: MOBILE EVENT HANDLERS
  // ==========================================================================
  
  /**
   * Handler para mudanças de estado do AudioContext.
   */
  _onAudioContextStateChange(state) {
    this.diagnostics.logAudioContext('STATE_CHANGED', { state });
    
    if (state === 'suspended' && this.recognitionActive) {
      this._log('AUDIOCONTEXT_SUSPENDED_WHILE_ACTIVE');
      // Tentar resume
      this.audioContextManager.unlock();
    }
  }
  
  /**
   * Handler para erros de áudio.
   */
  _onAudioError(error) {
    this.diagnostics.logError('AUDIO', error);
    this.metrics.errors++;
    
    // Tentar recovery se possível
    if (this.recognitionActive) {
      this.recoveryEngine.recover('audioContext');
    }
  }
  
  /**
   * Handler quando recovery do STT se esgota.
   */
  _onSTTRecoveryExhausted(reason) {
    this.diagnostics.log(DiagnosticLevel.WARNING, 'STT_RECOVERY_EXHAUSTED', { reason });
    this.callbacks.onError('Voice recognition stopped. Please tap microphone again.');
    this.stop();
  }
  
  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  
  /**
   * Inicializa o pipeline e começa a escutar.
   * SPRINT 2: Integrado com Mobile Audio Engine
   * SPRINT 3A FIX: Recebe idioma como parâmetro (Single Source of Truth)
   * 
   * @param {string} language - Código do idioma para SpeechRecognition (ex: 'en-US', 'pt-BR')
   */
  async start(language = 'en-US') {
    try {
      // SPRINT 3A: Armazenar idioma como fonte de verdade para este turno
      this._currentLanguage = language;
      
      this.diagnostics.startTimer('pipelineStart');
      this._log('PIPELINE_STARTING', { 
        turnNumber: this._turnCounter || 1,
        platform: this.platform.platformId,
        language: this._currentLanguage,  // Log do idioma
      });
      
      // SPRINT 3A: Iniciar sessão de logging de eventos mobile
      mobileEventLogger.startSession();
      mobileEventLogger.logEvent('pipeline_start', {
        turnNumber: this._turnCounter || 1,
        platform: this.platform.platformId,
        language: this._currentLanguage,
      });
      
      // Verificar suporte
      if (!this._checkBrowserSupport()) {
        throw new Error('Browser não suporta Web Speech API ou getUserMedia');
      }
      
      // SPRINT 2: Inicializar áudio usando Mobile Audio Engine
      if (!this.audioContext || this.audioContext.state === 'closed') {
        await this._initAudioMobile();
        await this._estimateNoiseFloor();
      } else if (this.audioContext.state === 'suspended') {
        // SPRINT 2: Tentar unlock se suspenso
        this.diagnostics.log(DiagnosticLevel.INFO, 'AUDIOCONTEXT_RESUMING');
        await this.audioContextManager.unlock();
      }
      
      // ====================================================================
      // SPRINT 1: SEMPRE criar nova instância de SpeechRecognition
      // ====================================================================
      
      // Destruir instância anterior se existir
      if (this.recognition) {
        this._log('RECOGNITION_RECREATING', { reason: 'new turn' });
        this._destroyRecognition();
      }
      
      // SPRINT 2: Reset recovery manager para novo turno
      this.sttRecoveryManager.reset();
      
      // Criar nova instância LIMPA
      this._initSpeechRecognition();
      
      // Garantir que transcript está limpo
      this.transcript = '';
      this.interimTranscript = '';
      
      // Iniciar componentes
      this.stateMachine.startListening();
      this.decisionEngine.start();
      
      // Iniciar STT
      this._startRecognition();
      
      // Iniciar loop de processamento de áudio
      this._startAudioProcessing();
      
      this.metrics.sessionsStarted++;
      
      const startTime = this.diagnostics.endTimer('pipelineStart');
      
      this._log('PIPELINE_STARTED', {
        noiseFloor: this.noiseFloor.toFixed(4),
        state: this.stateMachine.currentState,
        turnNumber: this._turnCounter || 1,
        transcriptEmpty: this.transcript === '',
        startTimeMs: Math.round(startTime),
        actualSampleRate: this.audioContext?.sampleRate,
      });
      
      return true;
      
    } catch (error) {
      this._log('PIPELINE_START_ERROR', { error: error.message });
      this.callbacks.onError(error.message);
      this.stateMachine.error(error);
      return false;
    }
  }
  
  /**
   * Para o pipeline completamente.
   */
  stop() {
    this._log('PIPELINE_STOPPING');
    
    // SPRINT 3A: Finalizar sessão de logging mobile
    const eventSession = mobileEventLogger.endSession();
    if (eventSession?.summary) {
      this._log('MOBILE_EVENT_SESSION_SUMMARY', {
        pipelineStatus: eventSession.summary.pipelineStatus,
        failurePoint: eventSession.summary.failurePoint,
        missingEvents: eventSession.summary.missingEvents,
        totalEvents: eventSession.summary.totalEvents,
      });
    }
    
    // Parar audio processing
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Parar Speech Recognition
    this._stopRecognition();
    
    // Parar componentes
    this.decisionEngine.stop();
    this.stateMachine.idle();
    
    // Liberar recursos de áudio
    this._releaseAudio();
    
    this._log('PIPELINE_STOPPED');
  }
  
  /**
   * Pausa temporariamente (mantém recursos).
   */
  pause() {
    this._log('PIPELINE_PAUSING');
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    this._stopRecognition();
    this.decisionEngine.stop();
  }
  
  /**
   * Retoma após pausa.
   */
  resume() {
    this._log('PIPELINE_RESUMING');
    
    if (this.stateMachine.canStartListening()) {
      this.stateMachine.startListening();
      this.decisionEngine.start();
      this._startRecognition();
      this._startAudioProcessing();
    }
  }
  
  /**
   * Reinicia para novo turno (após resposta de Lira).
   * SPRINT 1 FIX: Agora destrói e recria a instância de SpeechRecognition
   * para garantir um ciclo de vida completamente isolado.
   */
  resetForNewTurn() {
    this._log('PIPELINE_RESET_FOR_NEW_TURN', { 
      previousTranscript: this.transcript?.substring(0, 30) 
    });
    
    // ====================================================================
    // SPRINT 1: CICLO DE VIDA ISOLADO DO SPEECHRECOGNITION
    // Cada turno deve começar com uma instância NOVA e LIMPA
    // ====================================================================
    
    // 1. Destruir instância atual de recognition
    this._destroyRecognition();
    
    // 2. Limpar transcrição
    this.transcript = '';
    this.interimTranscript = '';
    
    // 3. Resetar decision engine
    this.decisionEngine.reset();
    
    // 4. Resetar volume smoothing
    this.smoothedVolume = 0;
    this.volumeHistory = [];
    
    // 5. Incrementar contador de turno para logs
    this._turnCounter = (this._turnCounter || 0) + 1;
    
    this._log('TURN_RESET_COMPLETE', { 
      turnNumber: this._turnCounter,
      transcriptCleared: true,
      recognitionDestroyed: true
    });
  }
  
  /**
   * SPRINT 1: Destrói completamente a instância de SpeechRecognition.
   * Remove todos os event listeners e libera recursos.
   */
  _destroyRecognition() {
    if (!this.recognition) {
      this._log('RECOGNITION_DESTROY_SKIPPED', { reason: 'no instance' });
      return;
    }
    
    this._log('RECOGNITION_DESTROYING');
    
    // 1. Parar qualquer reconhecimento ativo
    this.recognitionActive = false;
    
    try {
      this.recognition.abort();
    } catch (e) {
      // Ignorar erros ao abortar
    }
    
    // 2. Remover TODOS os event listeners (previne memory leaks)
    this.recognition.onresult = null;
    this.recognition.onerror = null;
    this.recognition.onend = null;
    this.recognition.onspeechstart = null;
    this.recognition.onspeechend = null;
    this.recognition.onaudiostart = null;
    this.recognition.onaudioend = null;
    this.recognition.onsoundstart = null;
    this.recognition.onsoundend = null;
    this.recognition.onnomatch = null;
    
    // 3. Destruir referência
    this.recognition = null;
    
    this._log('RECOGNITION_DESTROYED');
  }
  
  // ==========================================================================
  // INICIALIZAÇÃO DE ÁUDIO
  // ==========================================================================
  
  _checkBrowserSupport() {
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasSpeechRecognition = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    const hasAudioContext = !!(window.AudioContext || window.webkitAudioContext);
    
    this.diagnostics.log(DiagnosticLevel.INFO, 'BROWSER_SUPPORT_CHECK', {
      hasGetUserMedia,
      hasSpeechRecognition,
      hasAudioContext,
    });
    
    return hasGetUserMedia && hasSpeechRecognition && hasAudioContext;
  }
  
  /**
   * SPRINT 2: Inicialização de áudio Mobile-First.
   * Usa AudioContextManager e MediaStreamManager para maior robustez.
   */
  async _initAudioMobile() {
    this.diagnostics.startTimer('audioContextCreation');
    
    try {
      // 1. Criar e desbloquear AudioContext
      this.audioContext = await this.audioContextManager.create();
      
      // Verificar se precisa unlock (iOS/PWA)
      if (!this.audioContextManager.isReady()) {
        this._log('AUDIOCONTEXT_NEEDS_UNLOCK');
        const unlocked = await this.audioContextManager.unlock();
        if (!unlocked) {
          this._log('AUDIOCONTEXT_UNLOCK_DEFERRED', { state: this.audioContext?.state });
          // Continuar mesmo sem unlock - será feito no próximo gesto
        }
      }
      
      this.diagnostics.endTimer('audioContextCreation');
      this.diagnostics.logAudioContext('CREATED', {
        state: this.audioContext.state,
        sampleRate: this.audioContext.sampleRate,
        requestedSampleRate: this.config.sampleRate,
      });
      
      // 2. Obter MediaStream
      this.diagnostics.startTimer('mediaStreamAcquisition');
      this.mediaStream = await this.mediaStreamManager.acquire();
      this.diagnostics.endTimer('mediaStreamAcquisition');
      
      this.diagnostics.logMediaStream('ACQUIRED', {
        tracks: this.mediaStream.getTracks().length,
      });
      
      // 3. Configurar Analyser com sampleRate REAL
      const actualSampleRate = this.audioContextManager.getSampleRate();
      const fftConfig = this.platformConfig.adjustForSampleRate(actualSampleRate);
      
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = fftConfig.fftSize || this.config.fftSize;
      this.analyser.smoothingTimeConstant = 0.3;
      
      // 4. Conectar stream ao analyser
      this.mediaSource = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.mediaSource.connect(this.analyser);
      
      this._log('AUDIO_INITIALIZED_MOBILE', {
        actualSampleRate,
        requestedSampleRate: this.config.sampleRate,
        fftSize: this.analyser.fftSize,
        audioContextState: this.audioContext.state,
        platform: this.platform.platformId,
      });
      
    } catch (error) {
      this.diagnostics.logError('AUDIO_INIT', error);
      this.metrics.errors++;
      throw error;
    }
  }
  
  /**
   * LEGACY: Inicialização de áudio original (mantida para compatibilidade).
   * Em novos fluxos, usar _initAudioMobile().
   */
  async _initAudio() {
    // Redirecionar para versão mobile
    return this._initAudioMobile();
  }
  
  async _estimateNoiseFloor() {
    return new Promise((resolve) => {
      const samples = [];
      const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      const startTime = Date.now();
      
      const measure = () => {
        this.analyser.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(
          dataArray.reduce((sum, val) => sum + val * val, 0) / dataArray.length
        ) / 255;
        
        samples.push(rms);
        
        if (Date.now() - startTime < this.config.noiseFloorEstimationTime) {
          requestAnimationFrame(measure);
        } else {
          // Usar percentil 50 como noise floor (mediana)
          samples.sort((a, b) => a - b);
          this.noiseFloor = samples[Math.floor(samples.length * 0.5)] || 0.01;
          this.adaptiveNoiseFloor = this.noiseFloor;
          
          this._log('NOISE_FLOOR_ESTIMATED', {
            noiseFloor: this.noiseFloor.toFixed(4),
            samples: samples.length
          });
          
          resolve();
        }
      };
      
      requestAnimationFrame(measure);
    });
  }
  
  _releaseAudio() {
    // SPRINT 1: Usar método dedicado para destruir recognition
    this._destroyRecognition();
    
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    
    if (this.mediaSource) {
      this.mediaSource.disconnect();
      this.mediaSource = null;
    }
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.analyser = null;
    
    // Limpar volumeHistory para evitar memory leak
    this.volumeHistory = [];
    
    // SPRINT 1: Limpar transcript também
    this.transcript = '';
    this.interimTranscript = '';
  }
  
  // ==========================================================================
  // SPEECH RECOGNITION (STT)
  // ==========================================================================
  
  /**
   * SPRINT 1: Cria uma nova instância de SpeechRecognition.
   * Cada turno DEVE ter uma instância nova para evitar acúmulo de transcript.
   * SPRINT 3A FIX: Usa this._currentLanguage como Single Source of Truth
   */
  _initSpeechRecognition() {
    const turnNum = this._turnCounter || 1;
    const language = this._currentLanguage || 'en-US';
    
    this._log('STT_INITIALIZING', { 
      turnNumber: turnNum,
      language: language,  // SPRINT 3A: Log do idioma
      previousTranscript: this.transcript || '(empty)'
    });
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    // Criar NOVA instância
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = language;  // SPRINT 3A FIX: Usa idioma dinâmico
    this.recognition.maxAlternatives = 1;
    
    this._log('STT_LANGUAGE_SET', { 
      language: language,
      recognitionLang: this.recognition.lang,
    });
    
    // ====================================================================
    // SPRINT 1: Handler de resultados com logs detalhados e proteção
    // contra acúmulo de turnos anteriores
    // SPRINT 3A: Instrumentação completa para investigação mobile
    // ====================================================================
    this.recognition.onresult = (event) => {
      const isFinalResult = event.results[event.results.length - 1]?.isFinal;
      
      // SPRINT 3A: Log de evento para investigação mobile
      mobileEventLogger.logRecognitionEvent(
        isFinalResult ? 'onresult_final' : 'onresult_interim',
        {
          turnNumber: turnNum,
          resultIndex: event.resultIndex,
          resultsLength: event.results.length,
          isFinal: isFinalResult,
        }
      );
      
      // Log detalhado para debug
      this._log('STT_ONRESULT', {
        turnNumber: turnNum,
        resultIndex: event.resultIndex,
        resultsLength: event.results.length,
        currentTranscript: this.transcript?.substring(0, 30) || '(empty)'
      });
      
      let interim = '';
      let final = '';
      
      // SPRINT 1 FIX: Começar SEMPRE do resultIndex para evitar reprocessar
      // resultados anteriores da mesma sessão
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        const confidence = result[0].confidence;
        
        if (result.isFinal) {
          final += text;
          
          this._log('STT_FINAL_RESULT', { 
            turnNumber: turnNum,
            text: text.substring(0, 50),
            confidence: confidence?.toFixed(2),
            willAccumulateTo: (this.transcript + final).substring(0, 50)
          });
          
          // Notificar Decision Engine
          this.decisionEngine.onSTTFinalResult(this.transcript + final, confidence);
          
          // SPRINT 3A: Log de sinal para Decision Engine
          mobileEventLogger.logTurnDecisionEvent('receiveSignal', {
            signal: 'STT_FINAL_RESULT',
            transcript: (this.transcript + final).substring(0, 30),
          });
          
        } else {
          interim += text;
          
          // Notificar Decision Engine (interim)
          this.decisionEngine.onSTTInterimResult(this.transcript + interim);
        }
      }
      
      if (final) {
        this.transcript += final;
        this.callbacks.onTranscript(this.transcript);
        
        this._log('STT_TRANSCRIPT_UPDATED', {
          turnNumber: turnNum,
          newTranscript: this.transcript.substring(0, 50)
        });
        
        // Atualizar state machine
        this.stateMachine.transcriptFinal(this.transcript);
        
        // SPRINT 3A: Log de transição
        mobileEventLogger.logStateMachineEvent('transition', {
          trigger: 'transcriptFinal',
          state: this.stateMachine.currentState,
        });
      }
      
      if (interim) {
        this.interimTranscript = interim;
        this.callbacks.onInterimTranscript(interim);
        
        // Atualizar state machine para TRANSCRIBING
        if (this.stateMachine.currentState === ConversationState.VOICE_DETECTED ||
            this.stateMachine.currentState === ConversationState.LISTENING) {
          this.stateMachine.transcribing(interim);
        }
      }
    };
    
    // SPRINT 3A: Handler onstart - primeiro evento na sequência
    this.recognition.onstart = () => {
      this._log('STT_ONSTART', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onstart', { turnNumber: turnNum });
    };
    
    // SPRINT 3A: Handler onaudiostart
    this.recognition.onaudiostart = () => {
      this._log('STT_AUDIOSTART_EVENT', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onaudiostart', { turnNumber: turnNum });
    };
    
    // SPRINT 3A: Handler onsoundstart
    this.recognition.onsoundstart = () => {
      this._log('STT_SOUNDSTART_EVENT', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onsoundstart', { turnNumber: turnNum });
    };
    
    // Handler: Início de fala
    this.recognition.onspeechstart = () => {
      this._log('STT_SPEECH_START_EVENT', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onspeechstart', { turnNumber: turnNum });
    };
    
    // Handler: Fim de fala (CRÍTICO - sinal confiável em desktop, inconsistente em mobile!)
    this.recognition.onspeechend = () => {
      this._log('STT_SPEECH_END_EVENT', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onspeechend', { turnNumber: turnNum });
      
      this.decisionEngine.onSpeechEndEvent();
      mobileEventLogger.logTurnDecisionEvent('receiveSignal', { signal: 'SPEECH_END_EVENT' });
    };
    
    // SPRINT 3A: Handler onsoundend
    this.recognition.onsoundend = () => {
      this._log('STT_SOUNDEND_EVENT', { turnNumber: turnNum });
      mobileEventLogger.logRecognitionEvent('onsoundend', { turnNumber: turnNum });
    };
    
    // SPRINT 3A: Handler: Fim do áudio (SEMPRE emitido em todas plataformas!)
    // Este evento é crucial para mobile onde onspeechend frequentemente não é emitido.
    this.recognition.onaudioend = () => {
      this._log('STT_AUDIO_END_EVENT', { 
        turnNumber: turnNum,
        platform: this.platform.platformId,
      });
      mobileEventLogger.logRecognitionEvent('onaudioend', { 
        turnNumber: turnNum,
        platform: this.platform.platformId,
      });
      
      this.decisionEngine.onAudioEndEvent();
      mobileEventLogger.logTurnDecisionEvent('receiveSignal', { signal: 'AUDIO_END_EVENT' });
    };
    
    // Handler: Erro - SPRINT 2: Logging melhorado
    this.recognition.onerror = (event) => {
      this._log('STT_ERROR', { 
        turnNumber: turnNum,
        error: event.error 
      });
      
      // SPRINT 3A: Log de erro para investigação
      mobileEventLogger.logRecognitionEvent('onerror', {
        turnNumber: turnNum,
        error: event.error,
      });
      
      this.diagnostics.logSTT('ERROR', { 
        error: event.error,
        turnNumber: turnNum,
      });
      
      // Erros não-fatais
      if (event.error === 'no-speech' || event.error === 'aborted') {
        return;
      }
      
      // Erros fatais
      if (event.error === 'not-allowed') {
        this.diagnostics.logPermission('denied', new Error('Permission denied'));
        this.callbacks.onError('Permissão de microfone negada');
        this.stop();
        return;
      }
      
      // SPRINT 2: Usar recovery manager para outros erros
      if (event.error === 'network' || event.error === 'service-not-allowed') {
        this.diagnostics.logRecovery('TRIGGERED', { reason: event.error });
        this.sttRecoveryManager.attemptRestart(this.recognition, event.error);
      }
    };
    
    // Handler: Fim (auto-restart) - SPRINT 2: Usa STTRecoveryManager
    // SPRINT 3A: Este é um evento crítico - deve ser o último na sequência
    this.recognition.onend = () => {
      this._log('STT_ENDED', { 
        turnNumber: turnNum,
        recognitionActive: this.recognitionActive 
      });
      
      // SPRINT 3A: Log crítico para investigação mobile
      mobileEventLogger.logRecognitionEvent('onend', {
        turnNumber: turnNum,
        recognitionActive: this.recognitionActive,
        stateMachineState: this.stateMachine.currentState,
        hasTranscript: !!this.transcript,
        transcriptLength: this.transcript?.length || 0,
      });
      
      this.diagnostics.logSTT('ENDED', { 
        turnNumber: turnNum,
        recognitionActive: this.recognitionActive,
      });
      
      // Auto-restart APENAS se ainda estamos no mesmo turno de escuta
      if (this.recognitionActive && this.stateMachine.isListening()) {
        // SPRINT 2: Usar recovery manager com backoff
        if (this.sttRecoveryManager.shouldAttemptRecovery()) {
          this.sttRecoveryManager.attemptRestart(this.recognition, 'onend')
            .then(success => {
              if (success) {
                this._log('STT_AUTO_RESTARTED', { turnNumber: turnNum });
                this.diagnostics.logSTT('RESTART', { turnNumber: turnNum });
              }
            });
        } else {
          this._log('STT_RECOVERY_SKIPPED', { 
            turnNumber: turnNum,
            reason: 'max attempts or already recovering',
          });
        }
      }
    };
    
    this._log('STT_INITIALIZED', { 
      turnNumber: turnNum,
      transcriptIsEmpty: this.transcript === '' 
    });
    
    this.diagnostics.logSTT('INITIALIZED', { turnNumber: turnNum });
  }
  
  _startRecognition() {
    if (!this.recognition || this.recognitionActive) return;
    
    this.diagnostics.startTimer('sttInitialization');
    
    try {
      this.recognition.start();
      this.recognitionActive = true;
      this._log('STT_STARTED');
      this.diagnostics.logSTT('STARTED');
      this.diagnostics.endTimer('sttInitialization');
    } catch (e) {
      this._log('STT_START_FAILED', { error: e.message });
      this.diagnostics.logSTT('START_FAILED', { error: e.message });
    }
  }
  
  _stopRecognition() {
    if (!this.recognition) return;
    
    this.recognitionActive = false;
    
    try {
      this.recognition.stop();
      this._log('STT_STOPPED');
      this.diagnostics.logSTT('STOPPED');
    } catch (e) {
      // Ignorar erros ao parar
    }
  }
  
  // ==========================================================================
  // PROCESSAMENTO DE ÁUDIO (VAD)
  // ==========================================================================
  
  _startAudioProcessing() {
    if (this.animationFrameId) return;
    
    const process = () => {
      this._processAudioFrame();
      this.animationFrameId = requestAnimationFrame(process);
    };
    
    this.animationFrameId = requestAnimationFrame(process);
  }
  
  _processAudioFrame() {
    if (!this.analyser || !this.stateMachine.isListening()) return;
    
    // Obter dados de frequência
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);
    
    // Calcular RMS
    const rms = Math.sqrt(
      dataArray.reduce((sum, val) => sum + val * val, 0) / dataArray.length
    ) / 255;
    
    // Normalizar removendo noise floor
    const normalizedVolume = Math.max(0, rms - this.adaptiveNoiseFloor);
    
    // Smoothing
    this.smoothedVolume = this.smoothedVolume * 0.7 + normalizedVolume * 0.3;
    
    // Análise de frequência de voz
    const voiceAnalysis = this._analyzeVoiceFrequency(dataArray);
    
    // Determinar estado de fala
    const isSpeechLikely = this.smoothedVolume > this.config.vadSpeechThreshold && voiceAnalysis.isVoice;
    const isSilence = this.smoothedVolume < this.config.vadSilenceThreshold;
    
    // Notificar callbacks de volume
    this.callbacks.onVolumeChange(this.smoothedVolume, isSpeechLikely);
    
    // Atualizar Decision Engine
    if (isSpeechLikely) {
      this.decisionEngine.onVoiceDetected(this.smoothedVolume, voiceAnalysis.isVoice);
      
      // Atualizar state machine
      if (this.stateMachine.currentState === ConversationState.LISTENING) {
        this.stateMachine.voiceDetected();
      }
      
    } else if (isSilence) {
      const silenceDuration = this._calculateSilenceDuration();
      this.decisionEngine.onSilenceDetected(this.smoothedVolume, silenceDuration);
    }
    
    // Atualizar noise floor adaptativo
    if (!isSpeechLikely && !isSilence) {
      this.adaptiveNoiseFloor = this.adaptiveNoiseFloor * 0.99 + rms * 0.01;
      this.adaptiveNoiseFloor = Math.max(this.adaptiveNoiseFloor, this.noiseFloor * 0.8);
    }
  }
  
  _analyzeVoiceFrequency(dataArray) {
    const binCount = dataArray.length;
    const nyquist = this.audioContext.sampleRate / 2;
    const binSize = nyquist / binCount;
    
    const minBin = Math.floor(this.config.voiceFreqMin / binSize);
    const maxBin = Math.ceil(this.config.voiceFreqMax / binSize);
    
    let voiceEnergy = 0;
    let totalEnergy = 0;
    
    for (let i = 0; i < binCount; i++) {
      const energy = dataArray[i] * dataArray[i];
      totalEnergy += energy;
      
      if (i >= minBin && i <= maxBin) {
        voiceEnergy += energy;
      }
    }
    
    const ratio = totalEnergy > 0 ? voiceEnergy / totalEnergy : 0;
    const isVoice = ratio > this.config.voiceFreqRatio;
    
    return { ratio, isVoice, voiceEnergy, totalEnergy };
  }
  
  _calculateSilenceDuration() {
    // Usar timestamp do decision engine
    const lastSpeech = this.decisionEngine.timestamps.lastSpeech;
    return lastSpeech ? Date.now() - lastSpeech : 0;
  }
  
  // ==========================================================================
  // HANDLERS DE EVENTOS
  // ==========================================================================
  
  _onStateChange(event) {
    this._log('STATE_CHANGED', event);
    
    // Propagar para callback externo
    this.callbacks.onStateChange({
      state: event.currentState,
      previousState: event.previousState,
      label: this.stateMachine.getStateLabel(),
      ...event.metadata
    });
  }
  
  _onTurnComplete(result) {
    this._log('TURN_COMPLETE', {
      reason: result.reason,
      confidence: result.confidence.toFixed(3),
      transcript: result.transcript?.substring(0, 50)
    });
    
    // Atualizar state machine
    this.stateMachine.processing(result.transcript);
    
    // Atualizar métricas
    this.metrics.turnsCompleted++;
    this.metrics.avgTurnDuration = 
      (this.metrics.avgTurnDuration * (this.metrics.turnsCompleted - 1) + result.duration) 
      / this.metrics.turnsCompleted;
    
    // Propagar para callback externo
    this.callbacks.onTurnComplete({
      transcript: this.transcript,
      confidence: result.confidence,
      reason: result.reason,
      duration: result.duration,
      signals: result.signals,
    });
    
    // Parar captura de áudio
    this._stopRecognition();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  _onDecisionUpdate(update) {
    this.callbacks.onDecisionUpdate(update);
  }
  
  // ==========================================================================
  // API PÚBLICA
  // ==========================================================================
  
  /**
   * Força finalização do turno (manual).
   */
  forceFinishTurn() {
    this._log('FORCE_FINISH_TURN');
    
    const result = {
      transcript: this.transcript,
      confidence: 1.0,
      reason: 'MANUAL_FINISH',
      duration: this.decisionEngine.timestamps.turnStart 
        ? Date.now() - this.decisionEngine.timestamps.turnStart 
        : 0,
      signals: this.decisionEngine.getCurrentSignals(),
    };
    
    this._onTurnComplete(result);
  }
  
  /**
   * Retorna transcript atual.
   */
  getTranscript() {
    return this.transcript;
  }
  
  /**
   * Retorna estado atual.
   */
  getState() {
    return {
      state: this.stateMachine.currentState,
      label: this.stateMachine.getStateLabel(),
      isListening: this.stateMachine.isListening(),
      isProcessing: this.stateMachine.isProcessing(),
      isSpeaking: this.stateMachine.isSpeaking(),
    };
  }
  
  /**
   * Retorna métricas completas.
   */
  getMetrics() {
    return {
      pipeline: { ...this.metrics },
      stateMachine: this.stateMachine.getMetrics(),
      decisionEngine: this.decisionEngine.getMetrics(),
    };
  }
  
  /**
   * Retorna histórico de estados para auditoria.
   */
  getAuditLog() {
    return {
      stateHistory: this.stateMachine.getHistory(),
      decisionMetrics: this.decisionEngine.getMetrics(),
    };
  }
  
  /**
   * Define tipo de ambiente.
   * @param {'quiet'|'normal'|'noisy'} type
   */
  setEnvironmentType(type) {
    this.decisionEngine.setEnvironmentType(type);
  }
  
  /**
   * Habilita/desabilita modo contínuo.
   */
  setContinuousMode(enabled) {
    this.config.autoListenEnabled = enabled;
  }
  
  // ==========================================================================
  // LOGGING
  // ==========================================================================
  
  _log(event, data = {}) {
    if (!this.config.debugEnabled && !event.includes('ERROR')) {
      // Em produção, logar apenas eventos importantes
      const importantEvents = [
        'PIPELINE_STARTED', 'PIPELINE_STOPPED', 'TURN_COMPLETE',
        'STT_FINAL_RESULT', 'STATE_CHANGED', 'ERROR'
      ];
      
      if (!importantEvents.some(e => event.includes(e))) {
        return;
      }
    }
    
    const entry = {
      timestamp: new Date().toISOString(),
      event: `[VCCE] ${event}`,
      state: this.stateMachine?.currentState,
      ...data
    };
    
    console.log(entry.event, entry);
  }
}

export default VCCEPipeline;
export { ConversationState };
